import { Prisma, Project, UserRole } from '@prisma/client'

import type { FieldKey } from './field-key'

/**
 * The jobs a project names somebody for, and the shape they have in the
 * database.
 *
 * Three of them, each a column on `Project` and each the work of one role.
 * Said here once because every side needs the same three: the query that finds
 * a person's projects, the pane that staffs one, the guard that lets them
 * write, the cleanup when a role goes, and the PDF that prints who is on it.
 *
 * What a post *means* for access is `project-access.ts`; this is what it is.
 */
export const PROJECT_POSTS = {
  advisor: {
    /** The column on `Project`. Its `contactPerson` name is older than the
     *  word the business uses, and renaming it would rewrite the API. */
    column: 'contactPersonUserId',
    relation: 'contactPersonUser',
    /** The field the post is named and drawn by, on every surface that shows
     *  it: the row that sets it, the pill beside a name, the PDF. */
    field: 'contactPerson',
    /** The name the post's list filter goes by, in the URL and in the API's
     *  query. Held apart from `field` although the two read alike today: a
     *  field key is renamed when a label is reworded, and a saved link is not
     *  allowed to break over that. The advisor's is the oldest of the three
     *  and names the column rather than the word the business uses. */
    param: 'contactPerson',
    role: UserRole.SALES,
    /** The post that decides about the project itself — who holds the others,
     *  who else works on it, whether it goes. One post carries it. */
    decides: true,
  },
  projectManager: {
    column: 'projectManagerUserId',
    relation: 'projectManagerUser',
    field: 'projectManager',
    param: 'projectManager',
    role: UserRole.PROJECT_MANAGER,
  },
  siteManager: {
    column: 'siteManagerUserId',
    relation: 'siteManagerUser',
    field: 'siteManager',
    param: 'siteManager',
    role: UserRole.SITE_MANAGER,
  },
  // The column and relation names are the generated client's, so a typo here
  // is a build error rather than a Prisma error at the first write.
} as const satisfies Record<
  string,
  {
    column: keyof Project & string
    relation: keyof Prisma.ProjectInclude & string
    field: FieldKey
    param: string
    role: UserRole
    decides?: true
  }
>

/** One of the jobs a project names somebody for. */
export type ProjectPost = keyof typeof PROJECT_POSTS

export const PROJECT_POST_KEYS = Object.keys(PROJECT_POSTS) as ProjectPost[]

/** The field a post is named and drawn by. */
export const postField = (post: ProjectPost): FieldKey =>
  PROJECT_POSTS[post].field

/** The role whose work a post is. An account without it is not offered for
 *  the post, and `UsersService` clears the post when the role goes. */
export const postRole = (post: ProjectPost): UserRole =>
  PROJECT_POSTS[post].role

/** The posts that decide about the project itself. The flag on the table is
 *  what says which; `project-access.spec.ts` holds it to exactly one. */
export const DECIDING_POSTS = PROJECT_POST_KEYS.filter(
  post => 'decides' in PROJECT_POSTS[post],
)

export const DECIDING_POST: ProjectPost = DECIDING_POSTS[0]

/**
 * A record keyed by the posts' own column names.
 *
 * What a row read back, a Prisma `select` and a `where` are shaped like, so a
 * caller names the shape rather than writing out the three columns. Building
 * one is `byPostColumn` in `backend/src/utils/project-post-query.ts`, which is
 * where the query shapes live.
 */
export type ByPostColumn<V> = {
  [P in ProjectPost as (typeof PROJECT_POSTS)[P]['column']]: V
}

/** What a project holds for each post: a user id, or null for an empty one. */
export type ProjectPosts = Partial<ByPostColumn<number | null>>

/** The posts a project names this person for, in the posts' own order. */
export const postsHeldBy = (
  project: ProjectPosts,
  userId: number | null | undefined,
): ProjectPost[] =>
  userId == null
    ? []
    : PROJECT_POST_KEYS.filter(
        post => project[PROJECT_POSTS[post].column] === userId,
      )

/** Whether a project names anybody at all. One that names nobody is the
 *  administration's, and every scoped role is refused it. */
export const isStaffed = (project: ProjectPosts): boolean =>
  PROJECT_POST_KEYS.some(post => project[PROJECT_POSTS[post].column] != null)

/** The ids a project's posts are held by, each post once and empties dropped.
 *  What a caller resolves to people in one read. */
export const postHolderIds = (project: ProjectPosts): number[] =>
  PROJECT_POST_KEYS.map(post => project[PROJECT_POSTS[post].column]).filter(
    (id): id is number => id != null,
  )
